import { get, post, put, remove } from "../helpers/apiHelper";
import { Api } from "../constants";
import {
  ApiResponse,
  UserDeleteData,
  UserDetailsFetchRequest,
  UserDetailsFetchResponse,
  UserDetailsUpdateRequest,
  UserDetailsUpdateResponse,
  UserUpdateDetails,
  UserStatusUpdateRequest,
  UserOnboardingRequest,
  UserOnboardingResponse,
  UserFavouritesUpdateRequest,
  UserFavouritesFetchRequest,
  UserFavouritesFetchResponse,
  ReportedUsersFetchResponse,
  ReportUserRequest,
  ReportUserResponse,
} from "../types";
import { ERROR_MESSAGES } from "../utils/constants";

export const getUserDetails = async (
  data: UserDetailsFetchRequest
): Promise<UserDetailsFetchResponse> => {
  let url = `${Api.user}/${data.userId}`;
  if (data.lat && data.lon) {
    url += `?lat=${data.lat}&lon=${data.lon}`;
  }
  const result = await get<UserDetailsFetchResponse>(url);

  if (
    result &&
    typeof result === "object" &&
    "status" in result &&
    result.status === "ERROR"
  ) {
    const errorMessage =
      result.message || ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS;
    const error = new Error(errorMessage) as any;
    error.response = {
      data: {
        status: result.status,
        statusCode: result.statusCode || 500,
        message: errorMessage,
      },
    };
    throw error;
  }

  if (!result || (typeof result === "object" && !("userId" in result))) {
    const errorMessage = ERROR_MESSAGES.FAILED_TO_FETCH_USER_DETAILS;
    const error = new Error(errorMessage) as any;
    error.response = {
      data: {
        status: result.status || "ERROR",
        statusCode: result.statusCode || 500,
        message: errorMessage,
      },
    };
    throw error;
  }

  return result as UserDetailsFetchResponse;
};

export const updateUserDetails = async (
  data: UserDetailsUpdateRequest
): Promise<UserDetailsUpdateResponse> => {
  const url = `${Api.user}/${data.userId}`;
  const userData = data.data as UserUpdateDetails;
  const formData = new FormData();

  const userPayload: any = {};
  if (userData?.userName !== undefined) {
    userPayload.name = userData.userName;
  }
  if (userData?.dob !== undefined) {
    userPayload.dob = userData.dob;
  }
  if (userData?.mobile !== undefined) {
    userPayload.mobile = userData.mobile;
  }
  const userBlob = new Blob([JSON.stringify(userPayload)], {
    type: "application/json",
  });
  formData.append("user", userBlob);
  if (userData?.image && userData.image instanceof File) {
    formData.append("image", userData.image);
  }
  return await put(url, formData);
};

export const deleteUser = async (userId: string): Promise<UserDeleteData> => {
  const url = `${Api.user}/${userId}`;
  return await remove<UserDeleteData>(url);
};

export const updateUserStatus = async (
  data: UserStatusUpdateRequest
): Promise<ApiResponse> => {
  const url = `${Api.user}/${data.userId}/status/${data.status}`;
  return await put(url);
};

export const completeUserOnboarding = async (
  userId: string,
  data: UserOnboardingRequest
): Promise<UserOnboardingResponse> => {
  const url = `${Api.user}/${userId}/onboarding`;
  return await put<UserOnboardingResponse>(url, data);
};

export const updateUserFavourites = async (
  data: UserFavouritesUpdateRequest
): Promise<ApiResponse> => {
  const url = `${Api.user}/${data.userId}/favourites/${data.itemId}`;
  return await put(url);
};

export const deleteUserFavourites = async (
  data: UserFavouritesUpdateRequest
): Promise<ApiResponse> => {
  const url = `${Api.user}/${data.userId}/favourites/${data.itemId}`;
  return await remove(url);
};

export const getUserFavourites = async (
  data: UserFavouritesFetchRequest
): Promise<UserFavouritesFetchResponse> => {
  let url = `${Api.user}/favourites/${data.userId}`;
  const queryParams = new URLSearchParams();
  if (data.lat) {
    queryParams.append("lat", data.lat.toString());
  }
  if (data.lon) {
    queryParams.append("lon", data.lon.toString());
  }
  if (queryParams.toString()) {
    url += `?${queryParams.toString()}`;
  }
  return await get<UserFavouritesFetchResponse>(url);
};

export const deleteUserProfilePic = async (
  userId: string
): Promise<ApiResponse> => {
  const url = `${Api.user}/${userId}/image`;
  return await remove(url);
};

export const getReportedUsers =
  async (): Promise<ReportedUsersFetchResponse> => {
    const url = `${Api.user}/reported`;
    return await get(url);
  };

export const reportUser = async (
  data: ReportUserRequest
): Promise<ReportUserResponse> => {
  const url = `${Api.user}/report`;
  return await post(url, data);
};
